import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, ExternalLink, type LucideIcon } from "lucide-react";
import { Link } from "wouter";

interface ModuleHeaderProps {
  icon: LucideIcon;
  iconColor?: string;
  title: string;
  description: string;
  recordCount?: number | null;
  recordLabel?: string;
  lastUpdate?: string | null;
  onRefresh?: () => void;
  refreshing?: boolean;
  docsHref?: string;
  isLoading?: boolean;
  extra?: React.ReactNode;
}

function fmtNum(n: number) {
  return n.toLocaleString("pt-BR");
}

function fmtDate(d: string) {
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function fmtRelative(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `há ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  return `há ${days}d`;
}

export default function ModuleHeader({
  icon: Icon,
  iconColor = "text-primary",
  title,
  description,
  recordCount,
  recordLabel = "registros",
  lastUpdate,
  onRefresh,
  refreshing,
  docsHref,
  isLoading,
  extra,
}: ModuleHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2" data-testid="module-header">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold" data-testid="text-module-title">{title}</h1>
            {isLoading ? (
              <Skeleton className="h-5 w-20" />
            ) : recordCount != null ? (
              <Badge variant="secondary" className="text-xs tabular-nums" data-testid="badge-record-count">
                {fmtNum(recordCount)} {recordLabel}
              </Badge>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
          {lastUpdate && (
            <p className="text-xs text-muted-foreground mt-0.5" title={fmtDate(lastUpdate)}>
              Última atualização {fmtRelative(lastUpdate)}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {extra}
        {onRefresh && (
          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="button-module-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Atualizar Agora
          </Button>
        )}
        {docsHref && (
          <Link href={docsHref}>
            <Button size="sm" variant="ghost" data-testid="button-module-docs">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Ver Documentação API
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
